import { useCallback, useEffect, useState } from 'react';
import { ProviderFeeRule, ProviderFeeSnapshot, providerFeeRulesApi } from '../api/providers';

type Props = {
  providerId: string;
  currencies: string[];
};

type RuleForm = Omit<ProviderFeeRule, 'id' | 'providerId'>;

const operationTypes: ProviderFeeRule['operationType'][] = [
  'OUTGOING_PAYMENT',
  'INCOMING_PAYMENT',
  'INTERNAL_TRANSFER',
  'DIGITAL_OUTGOING',
  'DIGITAL_INCOMING',
  'FX',
  'RETURN',
];

const emptyRule = (): RuleForm => ({
  operationType: 'OUTGOING_PAYMENT',
  paymentMethod: null,
  paymentCurrencyId: null,
  feeCurrencyId: null,
  calculationMode: 'FIXED',
  fixedAmount: 0,
  percentOfAmount: 0,
  minFee: 0,
  maxFee: 0,
  priority: 0,
  isActive: true,
  isExplicitZero: false,
  companyCompensatesShortfall: false,
  effectiveFrom: new Date().toISOString(),
  effectiveTo: null,
  description: null,
});

export default function ProviderTariffsPanel({ providerId, currencies }: Props) {
  const [rules, setRules] = useState<ProviderFeeRule[]>([]);
  const [snapshots, setSnapshots] = useState<ProviderFeeSnapshot[]>([]);
  const [form, setForm] = useState<RuleForm>(emptyRule);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [rulesResponse, snapshotsResponse] = await Promise.all([
        providerFeeRulesApi.list(providerId),
        providerFeeRulesApi.snapshots(providerId),
      ]);
      setRules(rulesResponse);
      setSnapshots(snapshotsResponse);
    } catch (error: any) {
      setMessage(error?.response?.data?.message || error?.message || 'Failed to load provider tariffs.');
    } finally {
      setLoading(false);
    }
  }, [providerId]);

  useEffect(() => {
    void load();
  }, [load]);

  const change = <K extends keyof RuleForm>(key: K, value: RuleForm[K]) => {
    setForm((previous) => ({ ...previous, [key]: value }));
  };

  const save = async () => {
    try {
      setSaving(true);
      setMessage('');
      const payload: RuleForm = {
        ...form,
        paymentMethod: form.paymentMethod || null,
        paymentCurrencyId: form.paymentCurrencyId || null,
        feeCurrencyId: form.feeCurrencyId || null,
        effectiveTo: form.effectiveTo || null,
        description: form.description?.trim() || null,
        fixedAmount: form.isExplicitZero ? 0 : Number(form.fixedAmount),
        percentOfAmount: form.isExplicitZero ? 0 : Number(form.percentOfAmount),
        minFee: form.isExplicitZero ? 0 : Number(form.minFee),
        maxFee: form.isExplicitZero ? 0 : Number(form.maxFee),
        priority: Number(form.priority),
      };
      if (editingId) {
        await providerFeeRulesApi.update(providerId, editingId, payload);
      } else {
        await providerFeeRulesApi.create(providerId, payload);
      }
      setForm(emptyRule());
      setEditingId(null);
      await load();
      setMessage('Provider tariff saved.');
    } catch (error: any) {
      setMessage(error?.response?.data?.message || error?.message || 'Failed to save provider tariff.');
    } finally {
      setSaving(false);
    }
  };

  const edit = (rule: ProviderFeeRule) => {
    const { id, providerId: ignoredProviderId, ...next } = rule;
    void id;
    void ignoredProviderId;
    setForm(next);
    setEditingId(rule.id);
    setMessage('');
  };

  const deactivate = async (rule: ProviderFeeRule) => {
    if (!window.confirm(`Deactivate tariff ${rule.operationType}?`)) return;
    try {
      await providerFeeRulesApi.deactivate(providerId, rule.id);
      await load();
    } catch (error: any) {
      setMessage(error?.response?.data?.message || error?.message || 'Failed to deactivate provider tariff.');
    }
  };

  const resolveSnapshot = async (
    snapshotId: string,
    resolution: 'POST_ACTUAL' | 'POST_EXPECTED' | 'WAIVE',
  ) => {
    try {
      await providerFeeRulesApi.resolveSnapshot(providerId, snapshotId, resolution);
      await load();
    } catch (error: any) {
      setMessage(error?.response?.data?.message || error?.message || 'Failed to resolve provider fee exception.');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-800">Provider tariffs</h2>
        <p className="mt-1 text-sm text-gray-600">A matching active tariff is mandatory before an operation can be processed. Explicit zero is a stored rule, not a fallback.</p>
      </div>
      {message && <div className="rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-800">{message}</div>}

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2">Operation</th>
              <th className="px-3 py-2">Method / currency</th>
              <th className="px-3 py-2">Formula</th>
              <th className="px-3 py-2">Company compensation</th>
              <th className="px-3 py-2">Period</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rules.map((rule) => (
              <tr key={rule.id}>
                <td className="px-3 py-2 font-medium">{rule.operationType}</td>
                <td className="px-3 py-2">{rule.paymentMethod || 'ANY'} / {rule.paymentCurrencyId || 'ANY'}</td>
                <td className="px-3 py-2 font-mono">
                  {rule.isExplicitZero ? 'ZERO' : `${rule.calculationMode}: ${rule.fixedAmount} + ${rule.percentOfAmount}`}
                  {rule.feeCurrencyId ? ` ${rule.feeCurrencyId}` : ''}
                </td>
                <td className="px-3 py-2">{rule.companyCompensatesShortfall ? 'Enabled' : 'No'}</td>
                <td className="px-3 py-2 whitespace-nowrap">{new Date(rule.effectiveFrom).toLocaleDateString()} — {rule.effectiveTo ? new Date(rule.effectiveTo).toLocaleDateString() : '∞'}</td>
                <td className="px-3 py-2 text-right">
                  <button type="button" onClick={() => edit(rule)} className="mr-3 text-indigo-700">Edit</button>
                  {rule.isActive && <button type="button" onClick={() => void deactivate(rule)} className="text-rose-700">Deactivate</button>}
                </td>
              </tr>
            ))}
            {!loading && rules.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-500">No provider tariffs configured.</td></tr>}
          </tbody>
        </table>
      </div>

      <div>
        <h3 className="mb-3 font-semibold text-gray-800">Recent provider fee settlements</h3>
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr><th className="px-3 py-2">Operation</th><th className="px-3 py-2">Expected / actual</th><th className="px-3 py-2">Compensation</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Updated</th><th className="px-3 py-2" /></tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {snapshots.map((snapshot) => (
                <tr key={snapshot.id}>
                  <td className="px-3 py-2">{snapshot.operationType}<div className="font-mono text-xs text-gray-400">{snapshot.businessObjectId}</div></td>
                  <td className="px-3 py-2 font-mono">{snapshot.expectedFeeAmount} / {snapshot.actualFeeAmount ?? '—'} {snapshot.feeCurrencyId}</td>
                  <td className="px-3 py-2">{snapshot.compensationAmount > 0 ? `${snapshot.compensationAmount} ${snapshot.feeCurrencyId}` : '—'}</td>
                  <td className="px-3 py-2">{snapshot.settlementStatus}{snapshot.exceptionReason ? <div className="text-xs text-rose-700">{snapshot.exceptionReason}</div> : null}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{new Date(snapshot.updatedAt).toLocaleString()}</td>
                  <td className="px-3 py-2 text-right">
                    {snapshot.settlementStatus === 'EXCEPTION' && (
                      <div className="flex flex-wrap justify-end gap-2">
                        {snapshot.actualFeeAmount != null && <button type="button" onClick={() => void resolveSnapshot(snapshot.id, 'POST_ACTUAL')} className="text-xs text-indigo-700">Post actual</button>}
                        <button type="button" onClick={() => void resolveSnapshot(snapshot.id, 'POST_EXPECTED')} className="text-xs text-indigo-700">Post expected</button>
                        <button type="button" onClick={() => void resolveSnapshot(snapshot.id, 'WAIVE')} className="text-xs text-rose-700">Waive</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {!loading && snapshots.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-500">No provider fee settlements yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 p-4">
        <h3 className="font-semibold text-gray-800">{editingId ? 'Edit provider tariff' : 'Add provider tariff'}</h3>
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          <Select label="Operation" value={form.operationType} onChange={(value) => change('operationType', value as ProviderFeeRule['operationType'])} options={operationTypes} />
          <Select label="Payment method" value={form.paymentMethod || ''} onChange={(value) => change('paymentMethod', (value || null) as RuleForm['paymentMethod'])} options={['', 'SWIFT', 'SEPA', 'INTERNAL', 'DIGITAL']} />
          <Select label="Payment currency" value={form.paymentCurrencyId || ''} onChange={(value) => change('paymentCurrencyId', value || null)} options={['', ...currencies]} />
          <Select label="Fee currency" value={form.feeCurrencyId || ''} onChange={(value) => change('feeCurrencyId', value || null)} options={['', ...currencies]} />
          <Select label="Calculation" value={form.calculationMode} onChange={(value) => change('calculationMode', value as RuleForm['calculationMode'])} options={['FIXED', 'PERCENT', 'PERCENT_PLUS_FIXED']} />
          <NumberField label="Priority" value={form.priority} onChange={(value) => change('priority', value)} />
          <NumberField label="Fixed amount" value={form.fixedAmount} disabled={form.isExplicitZero} onChange={(value) => change('fixedAmount', value)} />
          <NumberField label="Percent fraction" value={form.percentOfAmount} disabled={form.isExplicitZero} onChange={(value) => change('percentOfAmount', value)} />
          <NumberField label="Min fee" value={form.minFee} disabled={form.isExplicitZero} onChange={(value) => change('minFee', value)} />
          <NumberField label="Max fee (0 = no cap)" value={form.maxFee} disabled={form.isExplicitZero} onChange={(value) => change('maxFee', value)} />
          <label className="block text-sm"><span className="font-medium text-gray-700">Effective from</span><input type="datetime-local" value={form.effectiveFrom.slice(0, 16)} onChange={(event) => change('effectiveFrom', new Date(event.target.value).toISOString())} className="mt-1 w-full rounded border px-3 py-2" /></label>
          <label className="block text-sm"><span className="font-medium text-gray-700">Effective to</span><input type="datetime-local" value={form.effectiveTo?.slice(0, 16) || ''} onChange={(event) => change('effectiveTo', event.target.value ? new Date(event.target.value).toISOString() : null)} className="mt-1 w-full rounded border px-3 py-2" /></label>
        </div>
        <div className="mt-4 flex flex-wrap gap-5 text-sm">
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.isExplicitZero} onChange={(event) => change('isExplicitZero', event.target.checked)} /> Explicit zero fee</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.companyCompensatesShortfall} onChange={(event) => change('companyCompensatesShortfall', event.target.checked)} /> Company compensates a provider-fee shortfall</label>
        </div>
        <label className="mt-4 block text-sm"><span className="font-medium text-gray-700">Description</span><input value={form.description || ''} onChange={(event) => change('description', event.target.value)} className="mt-1 w-full rounded border px-3 py-2" /></label>
        <div className="mt-4 flex gap-3">
          <button type="button" onClick={() => void save()} disabled={saving} className="rounded-lg bg-indigo-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">{saving ? 'Saving…' : 'Save tariff'}</button>
          {editingId && <button type="button" onClick={() => { setEditingId(null); setForm(emptyRule()); }} className="rounded-lg border px-4 py-2 text-sm">Cancel</button>}
        </div>
      </div>
    </div>
  );
}

function Select({ label, value, options, onChange }: { label: string; value: string; options: string[]; onChange: (value: string) => void }) {
  return <label className="block text-sm"><span className="font-medium text-gray-700">{label}</span><select value={value} onChange={(event) => onChange(event.target.value)} className="mt-1 w-full rounded border px-3 py-2">{options.map((option) => <option key={option} value={option}>{option || 'ANY'}</option>)}</select></label>;
}

function NumberField({ label, value, disabled, onChange }: { label: string; value: number; disabled?: boolean; onChange: (value: number) => void }) {
  return <label className="block text-sm"><span className="font-medium text-gray-700">{label}</span><input type="number" min="0" step="any" disabled={disabled} value={value} onChange={(event) => onChange(Number(event.target.value) || 0)} className="mt-1 w-full rounded border px-3 py-2 disabled:bg-gray-100" /></label>;
}
